import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tag, Plus, X, Shield } from 'lucide-react';
import { LocationSelector } from './LocationSelector';

/**
 * Shared job create/edit form body (used inside both Dialogs).
 *
 * Props:
 *   value: complete job object { title, description, location, state, district, gemeinde,
 *          salaryBrutto, hoursPerWeek, workingHours, employmentType, categories, adminNote }
 *   onChange(nextJob): full replacement of job object
 *   availableCategories: string[]
 *   newCategoryInput / setNewCategoryInput: controlled state for "new category" input
 *   testIdPrefix: data-testid prefix for the inputs (default "job")
 */
export const JobForm = ({
  value,
  onChange,
  availableCategories = [],
  newCategoryInput,
  setNewCategoryInput,
  testIdPrefix = 'job',
}) => {
  const job = value || {};
  const set = (patch) => onChange({ ...job, ...patch });

  const toggleCategory = (cat) => {
    const cats = job.categories || [];
    set({
      categories: cats.includes(cat) ? cats.filter((c) => c !== cat) : [...cats, cat],
    });
  };

  const addCustomCategory = () => {
    const trimmed = (newCategoryInput || '').trim();
    if (!trimmed) return;
    const cats = job.categories || [];
    if (!cats.includes(trimmed)) {
      set({ categories: [...cats, trimmed] });
    }
    setNewCategoryInput('');
  };

  return (
    <div className="grid gap-4 py-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Job-Titel *</Label>
          <Input
            value={job.title || ''}
            onChange={(e) => set({ title: e.target.value })}
            placeholder="z.B. Barista (Teilzeit)"
            data-testid={`${testIdPrefix}-title-input`}
          />
        </div>
        <div className="space-y-2">
          <Label>Gehalt brutto pro Monat (€) *</Label>
          <Input
            type="number"
            value={job.salaryBrutto ?? ''}
            onChange={(e) => set({ salaryBrutto: e.target.value })}
            placeholder="650"
            data-testid={`${testIdPrefix}-salary-input`}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Beschreibung *</Label>
        <Textarea
          value={job.description || ''}
          onChange={(e) => set({ description: e.target.value })}
          placeholder="Kurze Beschreibung des Jobs..."
          rows={3}
          data-testid={`${testIdPrefix}-description-input`}
        />
      </div>

      <LocationSelector
        value={{ state: job.state, district: job.district, gemeinde: job.gemeinde }}
        onChange={(loc) => set(loc)}
        testIdPrefix={testIdPrefix}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Ortsbeschreibung *</Label>
          <Input
            value={job.location || ''}
            onChange={(e) => set({ location: e.target.value })}
            placeholder="z.B. Wien Innere Stadt"
            data-testid={`${testIdPrefix}-location-input`}
          />
        </div>
        <div className="space-y-2">
          <Label>Stunden/Woche *</Label>
          <Input
            type="number"
            value={job.hoursPerWeek ?? ''}
            onChange={(e) => set({ hoursPerWeek: e.target.value })}
            placeholder="20"
            data-testid={`${testIdPrefix}-hours-input`}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Dienstzeiten</Label>
        <Input
          value={job.workingHours || ''}
          onChange={(e) => set({ workingHours: e.target.value })}
          placeholder="z.B. Mo.-Fr. 09:00-21:00"
          data-testid={`${testIdPrefix}-working-hours-input`}
        />
      </div>

      <div className="space-y-2">
        <Label>Beschäftigungstyp *</Label>
        <Select
          value={job.employmentType || 'Teilzeit'}
          onValueChange={(value) => set({ employmentType: value })}
        >
          <SelectTrigger data-testid={`${testIdPrefix}-type-select`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Geringfügig">Geringfügig</SelectItem>
            <SelectItem value="Teilzeit">Teilzeit</SelectItem>
            <SelectItem value="Vollzeit">Vollzeit</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Categories */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Tag className="w-4 h-4" />
          Job-Kategorien
        </Label>

        {(job.categories || []).length > 0 && (
          <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-xl">
            {job.categories.map((cat) => (
              <Badge
                key={cat}
                className="bg-primary text-white px-3 py-1 cursor-pointer hover:bg-primary/80"
                onClick={() => toggleCategory(cat)}
                data-testid={`selected-category-${cat}`}
              >
                {cat}
                <X className="w-3 h-3 ml-1" />
              </Badge>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border border-slate-200 rounded-xl">
          {availableCategories
            .filter((cat) => !(job.categories || []).includes(cat))
            .map((cat) => (
              <Badge
                key={cat}
                variant="outline"
                className="cursor-pointer hover:bg-slate-100"
                onClick={() => toggleCategory(cat)}
                data-testid={`available-category-${cat}`}
              >
                {cat}
              </Badge>
            ))}
        </div>

        <div className="flex gap-2">
          <Input
            placeholder="Neue Kategorie..."
            value={newCategoryInput}
            onChange={(e) => setNewCategoryInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomCategory())}
            className="flex-1"
            data-testid={`${testIdPrefix}-new-category-input`}
          />
          <Button
            type="button"
            variant="outline"
            onClick={addCustomCategory}
            data-testid={`${testIdPrefix}-add-category-button`}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Admin Note (internal) */}
      <div className="space-y-2 pt-4 border-t border-slate-200">
        <Label className="flex items-center gap-2 text-amber-700">
          <Shield className="w-4 h-4" />
          Interne Notiz / Firma (nur für Admins sichtbar)
        </Label>
        <Textarea
          value={job.adminNote || ''}
          onChange={(e) => set({ adminNote: e.target.value })}
          placeholder="z.B. Firma XY, Ansprechpartner Hr. Müller, Tel: 0660..."
          rows={2}
          className="border-amber-200 bg-amber-50/50"
          data-testid={`${testIdPrefix}-admin-note-input`}
        />
        <p className="text-xs text-slate-500">Diese Info sehen nur Admins - nicht die Bewerber</p>
      </div>
    </div>
  );
};
